import {useCallback} from 'react';

import {useEditInfoTrackers} from '@/app/main/editInfo/useEditInfoTrackers';
import {useLoading} from '@/app/zustand/useLoading';
import {getErrorSchema} from '@/hooks/TrackingManager/utils/getErrorSchema';
import logError from '@/utils/errors/logError';

import {useResolveEmailMutation} from './api';
import {IResolveEmailResponse} from './entities';

const fileName = 'useEditInfoRepository';

export const useEditInfoRepository = ({parentId}: {parentId?: string}) => {
  const [triggerResolveEmail] = useResolveEmailMutation();
  const {trackApiEditInfoEmailOtpSuccess, trackApiEditInfoEmailOtpError} =
    useEditInfoTrackers({
      parentId,
    });
  const {startLoading, stopLoading} = useLoading();

  const handleResolveEmail = useCallback(
    async (token: string): Promise<IResolveEmailResponse | undefined> => {
      startLoading();
      try {
        const result = await triggerResolveEmail({token});
        if ('error' in result && result.error) {
          throw result.error;
        }
        trackApiEditInfoEmailOtpSuccess();
        return result.data;
      } catch (error) {
        const errorSchema = getErrorSchema(error);
        trackApiEditInfoEmailOtpError(
          errorSchema.error.message,
          errorSchema.error.code,
        );
        logError(error, {
          customTitle: 'EditInfo - resolve email token error',
          fileName,
          functionName: 'handleResolveEmail',
          extraTags: {userFlow: 'EditInfo'},
        });
        throw error;
      } finally {
        stopLoading();
      }
    },

    [
      startLoading,
      stopLoading,
      trackApiEditInfoEmailOtpError,
      trackApiEditInfoEmailOtpSuccess,
      triggerResolveEmail,
    ],
  );

  return {
    handleResolveEmail,
  };
};
